import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { AxiosResponse } from "axios";
import { decamelizeKeys } from "humps";
import { serialize } from "object-to-formdata";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { FormProvider, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { Button, FormContainer } from "@/components";
import {
	archiveProduct,
	createProduct,
	unarchiveProduct,
	updateProduct,
} from "../../api";
import {
	toBarcodeLabel,
	useCurrentStore,
	useCustomAttributes,
	useLocalStorage,
	usePrintLabels,
} from "../../hooks";
import { formSchema, type formSchemaType } from "./config";
import ProductFormCustomAttributes from "./custom-attributes";
import ProductFormGeneral from "./general";
import ProductFormLogistics from "./logistics";
import ProductFormPricing from "./pricing";

type ProductFormProps = {
	product?: Product;
	onCancel?: () => void;
	onSuccess?: (data: AxiosResponse<Product>) => void;
};

const ProductForm = ({ onCancel, onSuccess, product }: ProductFormProps) => {
	const store = useCurrentStore();
	const { mutate: printLabels } = usePrintLabels();
	const { data: customAttributesData } = useCustomAttributes({
		"q[storeIdEq]": store?.id,
	});
	const { t } = useTranslation();
	const methods = useForm<formSchemaType>({
		resolver: zodResolver(formSchema),
		defaultValues: {},
	});
	const { handleSubmit, setValue, reset } = methods;
	const toastId = useRef<string>(null);
	const pageTitle = useMemo(
		() => product?.name || t("product.newPageTitle"),
		[product, t],
	);
	const [defaultVatRateId] = useLocalStorage("defaultVatRateId");
	const queryClient = useQueryClient();

	const initialValues = useMemo(() => {
		// Sort custom attributes to ensure stable ordering
		const sortedCustomAttributes = customAttributesData?.data
			? [...customAttributesData.data].sort((a, b) => a.id.localeCompare(b.id))
			: [];

		const productCustomAttributesAttributes = sortedCustomAttributes.map(
			(customAttribute) => {
				const existingCustomAttribute = product?.productCustomAttributes?.find(
					(productCustomAttribute) =>
						productCustomAttribute.customAttribute.id === customAttribute.id,
				);

				return {
					id: existingCustomAttribute?.id,
					customAttributeId: customAttribute.id,
					value: existingCustomAttribute?.value,
				};
			},
		);

		const values = product
			? {
					...product,
					categoryId: product.category?.category?.id ?? product.category?.id,
					subCategoryId: product.category?.category
						? product.category?.id
						: undefined,
					manufacturerId: product.manufacturer?.id,
					storeId: store?.id,
					buyingAmount: product.buyingAmountCents,
					taxFreeAmount: product.taxFreeAmountCents,
					amount: product.amountCents,
					vatRateId: product.vatRate?.id,
					description: product.description || "",
					productCustomAttributes: product.productCustomAttributes,
					productCustomAttributesAttributes,
					images: (product.images ?? []).map((image) => ({
						name: (image.large ?? "").split("/").at(-1) ?? "",
						signed_id: image.signedId,
						thumbnail: image.thumbnail,
					})),
				}
			: {
					vatRateId: defaultVatRateId,
					productCustomAttributes: [],
					productCustomAttributesAttributes,
				};
		return values;
	}, [product, store, defaultVatRateId, customAttributesData]);

	const { mutate: createApiProduct } = useMutation({
		mutationFn: (newData: FormData) => createProduct(newData),
		onSuccess: (data) => {
			toast.success(t("global.saved"), { id: toastId?.current || undefined });
			onSuccess?.(data);
		},
		onMutate: () => {
			toastId.current = toast.loading(t("global.loading"));
		},
		onError: () => {
			toast.error(t("global.savingError"), {
				id: toastId?.current || undefined,
			});
		},
	});

	const invalidateProduct = useCallback(() => {
		queryClient.invalidateQueries({
			queryKey: ["products", { id: product?.id }],
		});
	}, [queryClient, product]);

	const { mutate: updateApiProduct } = useMutation({
		mutationFn: (newData: FormData) => updateProduct(product?.id, newData),
		onSuccess: (data) => {
			toast.success(t("global.saved"), { id: toastId?.current || undefined });
			invalidateProduct();
			onSuccess?.(data);
			reset(initialValues);
		},
		onMutate: () => {
			toastId.current = toast.loading(t("global.loading"));
		},
		onError: () => {
			toast.error(t("global.savingError"), {
				id: toastId?.current || undefined,
			});
		},
	});

	const { mutate: archiveApiProduct } = useMutation({
		mutationFn: () => archiveProduct(product?.id),
		onSuccess: invalidateProduct,
	});

	const { mutate: unarchiveApiProduct } = useMutation({
		mutationFn: () => unarchiveProduct(product?.id),
		onSuccess: invalidateProduct,
	});

	const onSubmit = useCallback(
		(data: formSchemaType) => {
			// Filter out custom attributes with no value and no existing id
			// Only send attributes that have been filled in or already exist in the database
			const filteredCustomAttributes =
				data.productCustomAttributesAttributes?.filter((attr) => {
					// Keep if it has an existing id (even if value is empty, to allow updates/deletions)
					if (attr?.id) return true;
					// Keep if it has a value (new attribute being created)
					if (attr?.value) return true;
					// Filter out attributes with no id and no value
					return false;
				});

			// Convert array to indexed object for Rails nested attributes
			const indexedCustomAttributes =
				filteredCustomAttributes &&
				Object.fromEntries(
					filteredCustomAttributes.map((attr, index) => [index, attr]),
				);

			// Images go as separate form parts below, not inside the product.
			const { images: _images, ...fields } = data;
			const productData = {
				...fields,
				productCustomAttributesAttributes: indexedCustomAttributes,
			};

			const formData = serialize({
				product: decamelizeKeys(productData),
			});

			if (data.images?.length) {
				data.images.forEach((item) => {
					if (item instanceof File) {
						formData.append("product[images][]", item);
					} else if (item.signed_id) {
						formData.append("product[images][]", item.signed_id);
					}
				});
			} else {
				formData.append("product[images][]", "");
			}

			if (product) {
				updateApiProduct(formData);
			} else {
				createApiProduct(formData);
			}
		},
		[product, updateApiProduct, createApiProduct],
	);

	const printTicket = useCallback(() => {
		if (product && store) {
			printLabels([toBarcodeLabel(product, store, 1)]);
		}
	}, [product, store, printLabels]);

	useEffect(() => {
		reset(initialValues);
	}, [initialValues, reset]);

	useEffect(() => {
		if (store) {
			setValue("storeId", store?.id);
		}
	}, [setValue, store]);

	return (
		<FormProvider {...methods}>
			<input {...methods.register("storeId")} type="hidden" />
			<FormContainer
				title={pageTitle}
				subtitle={t("product.pageSubtitle")}
				onSubmit={handleSubmit(onSubmit)}
				onCancel={onCancel}
				button={
					<div className="flex flex-row gap-4">
						{product?.archivedAt ? (
							<Button
								onClick={() => unarchiveApiProduct()}
								type="button"
								variant="ghost"
							>
								{t("global.unarchive")}
							</Button>
						) : (
							<Button
								onClick={() => archiveApiProduct()}
								type="button"
								color="red"
							>
								{t("global.archive")}
							</Button>
						)}
						<Button onClick={printTicket} type="button">
							{t("product.printLabel")}
						</Button>
					</div>
				}
			>
				<ProductFormGeneral />
				<ProductFormLogistics />
				<ProductFormPricing />
				<ProductFormCustomAttributes />
			</FormContainer>
		</FormProvider>
	);
};

export default ProductForm;
